import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { ImgixNextImage } from "@components/imgixNextImage";
import { ArrowRight } from "@icons/arrowRight";
import { ChevronRight } from "@icons/chevronRight";
import { tagular } from "@cohesion/tagular";
import { navigationData } from "./data";

export function MobileNav({
  alertDismissed = false,
  isOpen,
  hasSession = false,
  disableAlert = false,
}) {
  const [activePanel, setActivePanel] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  // Reset active panel when menu is closed
  useEffect(() => {
    if (!isOpen) {
      setActivePanel(null);
      setActiveSubmenu(null);
    }
  }, [isOpen]);

  // Calculate the correct top position for the mobile menu
  const getTopPosition = () => {
    if (disableAlert || alertDismissed) {
      return "top-[64px]";
    }
    return "top-[118px]";
  };

  // Calculate the correct height for the mobile menu
  const getHeightStyle = () => {
    if (disableAlert || alertDismissed) {
      return { height: "calc(100vh - 64px)" };
    }
    return { height: "calc(100vh - 118px)" };
  };

  const accountItems = [
    { title: "Saves", href: "https://www.lonelyplanet.com/profile/saves" },
    {
      title: "E-Library",
      href: "https://www.lonelyplanet.com/profile/library",
    },
    {
      title: "Account Settings",
      href: "https://www.lonelyplanet.com/profile/settings",
    },
    { title: "Logout", href: "/api/auth/logout" },
  ];

  const handlePanelOpen = panelName => {
    setActivePanel(panelName);

    // Track panel open
    tagular("click", {
      actionOutcome: "OPEN",
      outboundUrl: "null",
      webElement: {
        elementType: "TAB",
        location: "GLOBAL NAV",
        name: panelName.toUpperCase(),
        position:
          panelName === "Account"
            ? String(navigationData.items.length + 1)
            : String(
                navigationData.items.findIndex(
                  item => item.title === panelName
                ) + 1
              ),
      },
    });
  };

  const handleBackToMain = () => {
    // Track back to main menu
    if (activePanel) {
      tagular("click", {
        actionOutcome: "CLOSE",
        outboundUrl: "null",
        webElement: {
          elementType: "TAB",
          location: "GLOBAL NAV",
          name: activePanel.toUpperCase(),
          position:
            activePanel === "Account"
              ? String(navigationData.items.length + 1)
              : String(
                  navigationData.items.findIndex(
                    item => item.title === activePanel
                  ) + 1
                ),
        },
      });
    }

    setActivePanel(null);
  };

  const handleSubmenuToggle = (submenuName, sectionTitle) => {
    const newState = activeSubmenu === submenuName ? null : submenuName;
    setActiveSubmenu(newState);

    // Track submenu toggle
    const navItem = navigationData.items.find(
      item => item.title === activePanel
    );
    const sectionIndex =
      navItem?.submenu?.items?.findIndex(s => s.title === sectionTitle) || 0;
    const position = sectionIndex >= 0 ? String(sectionIndex + 1) : "1";

    tagular("click", {
      actionOutcome: newState ? "EXPAND" : "COLLAPSE",
      outboundUrl: "null",
      webElement: {
        elementType: "TAB",
        location: `SUB NAV - ${activePanel.toUpperCase()}`,
        name: sectionTitle.toUpperCase(),
        position,
      },
    });
  };

  return (
    <div
      className={`fixed inset-0 z-50 bg-white transform transition-transform duration-300 ease-in-out ${
        isOpen ? "translate-x-0" : "translate-x-full"
      } pointer-events-auto lg:hidden ${getTopPosition()}`}
      role="navigation"
      aria-label="Mobile navigation"
      style={getHeightStyle()}
    >
      {/* Main Menu Panel */}
      {!activePanel && (
        <div className="h-full overflow-y-auto">
          <nav className="w-full">
            <div>
              {navigationData.items.map(item => (
                <div
                  key={`nav-item-${item.title}`}
                  className="border-b border-black-200"
                >
                  <div className="container">
                    <button
                      type="button"
                      data-panel-trigger={item.title}
                      className="flex items-center justify-between w-full py-4 text-lg text-black uppercase hover:text-blue"
                      onClick={() => handlePanelOpen(item.title)}
                      aria-expanded={activePanel === item.title}
                      aria-controls={`panel-${item.title}`}
                    >
                      {item.title}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="border-b border-black-200">
                <div className="container">
                  <button
                    type="button"
                    data-panel-trigger="Account"
                    className="flex items-center justify-between w-full py-4 text-lg text-black uppercase hover:text-blue"
                    onClick={() => handlePanelOpen("Account")}
                    aria-expanded={activePanel === "Account"}
                    aria-controls="panel-Account"
                  >
                    Account
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* Submenu Panels */}
      {navigationData.items.map(item =>
        item.submenu && activePanel === item.title ? (
          <div
            key={`panel-${item.title}`}
            data-panel-content={item.title}
            className={`absolute inset-0 bg-white flex flex-col ${activePanel === item.title ? "translate-x-0" : "translate-x-full"}`}
            id={`panel-${item.title}`}
            role="region"
            aria-label={`${item.title} submenu`}
          >
            <div className="border-b border-black-200 bg-black-100">
              <div className="container flex items-center justify-between py-3">
                <button
                  type="button"
                  className="flex items-center text-black uppercase hover:text-blue"
                  onClick={handleBackToMain}
                  data-panel={item.title}
                  aria-label={`Back to ${item.title}`}
                >
                  <span className="inline-block mr-2 transform rotate-180">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                  Back
                </button>
                <span className="relative inline-block py-2 px-4 my-1 bg-blue-900 text-white font-semibold uppercase rotate-[-1.5deg]">
                  {item.title}
                </span>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto">
              <div className="space-y-1">
                {item.featured && (
                  <div className="border-b border-black-200">
                    <div className="container">
                      <a
                        href={item.featured.cta.url}
                        className="flex items-center justify-between w-full gap-4 py-4 text-lg text-black hover:text-blue group"
                        onClick={() => {
                          // Track featured click
                          if (item.featured.tracking) {
                            tagular("product_click", {
                              actionOutcome:
                                item.featured.tracking.actionOutcome ||
                                "INTERNALLINK",
                              outboundUrl: item.featured.cta.url,
                              product: {
                                brand: item.featured.tracking.product.brand,
                                location: `SUB NAV - ${item.title.toUpperCase()}`,
                                name: item.featured.cta.title.toUpperCase(),
                                text: item.featured.cta.title,
                              },
                            });
                          } else {
                            // Default tracking when no tracking object is provided
                            tagular("click", {
                              actionOutcome: "INTERNALLINK",
                              outboundUrl: item.featured.cta.url,
                              webElement: {
                                elementType: "LINK",
                                location: `SUB NAV - ${item.title.toUpperCase()}`,
                                name: item.featured.cta.title.toUpperCase(),
                                text: item.featured.cta.title,
                              },
                            });
                          }
                        }}
                      >
                        <ImgixNextImage
                          className="w-1/3 object-cover aspect-[4/3]"
                          src={item.featured.image}
                          width={400}
                          height={300}
                          alt={item.featured.title}
                          sizes="300px"
                          srcSet={`${item.featured.image} 300w`}
                        />
                        <div className="w-2/3 space-y-2">
                          <p className="text-xl text-black">
                            {item.featured.title}
                          </p>
                          <p className="text-sm text-black">
                            {item.featured.description}
                          </p>
                          <p className="text-xs underline uppercase group-hover:text-blue">
                            {item.featured.cta.title}
                          </p>
                        </div>
                      </a>
                    </div>
                  </div>
                )}

                {item.submenu.items.map(section => (
                  <div
                    key={`section-${item.title}-${section.title}`}
                    className="border-b border-black-200"
                  >
                    <div className="container">
                      {section.items.map(subItem => {
                        if (section.items.length === 1 && "url" in subItem) {
                          return (
                            <a
                              key={`subitem-${item.title}-${subItem.title}`}
                              href={subItem.url}
                              className="flex items-center justify-between w-full py-4 text-lg text-black uppercase hover:text-blue"
                              onClick={() => {
                                // Track subitem click
                                if (subItem.tracking) {
                                  tagular("product_click", {
                                    actionOutcome:
                                      subItem.tracking.actionOutcome ||
                                      "INTERNALLINK",
                                    outboundUrl: subItem.url,
                                    product: {
                                      brand: subItem.tracking.product.brand,
                                      location: `SUB NAV - ${item.title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                      name: subItem.title.toUpperCase(),
                                      position:
                                        section.items.indexOf(subItem) + 1,
                                      text: subItem.title,
                                    },
                                  });
                                } else {
                                  tagular("click", {
                                    actionOutcome: "INTERNALLINK",
                                    outboundUrl: subItem.url,
                                    webElement: {
                                      elementType: "LINK",
                                      location: `SUB NAV - ${item.title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                      name: subItem.title.toUpperCase(),
                                      position: String(
                                        section.items.indexOf(subItem) + 1
                                      ),
                                      text: subItem.title,
                                    },
                                  });
                                }
                              }}
                            >
                              {subItem.title}
                            </a>
                          );
                        }
                        return null;
                      })}

                      {(section.items.length > 1 ||
                        section.items.some(
                          sectionItem => "columns" in sectionItem
                        )) && (
                        <>
                          <button
                            type="button"
                            data-submenu-trigger
                            className={`flex items-center justify-between w-full py-4 text-lg text-black hover:text-blue uppercase ${activeSubmenu === section.title ? "active" : ""}`}
                            onClick={() =>
                              handleSubmenuToggle(section.title, section.title)
                            }
                            aria-expanded={activeSubmenu === section.title}
                            aria-controls={`submenu-${section.title}`}
                          >
                            {section.title}
                            <ChevronRight
                              className={`w-4 h-4 transform transition-transform duration-200 ${activeSubmenu === section.title ? "-rotate-90" : "rotate-90"}`}
                            />
                          </button>

                          <div
                            data-submenu-content
                            className={`${activeSubmenu === section.title ? "block" : "hidden"} pb-4 space-y-2`}
                            id={`submenu-${section.title}`}
                          >
                            {section.items.map(subItem => {
                              if ("url" in subItem) {
                                return (
                                  <a
                                    key={`subitem-detail-${item.title}-${section.title}-${subItem.title}`}
                                    href={subItem.url}
                                    className="block py-2 text-base text-black uppercase border-b hover:text-blue hover:border-blue hover:border-b border-black-200 last:border-b-0 last:hover:border-b-0 group"
                                    onClick={() => {
                                      // Track subitem detail click
                                      if (subItem.tracking) {
                                        tagular("product_click", {
                                          actionOutcome:
                                            subItem.tracking.actionOutcome ||
                                            "INTERNALLINK",
                                          outboundUrl: subItem.url,
                                          product: {
                                            brand:
                                              subItem.tracking.product.brand,
                                            location: `SUB NAV - ${item.title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                            name: subItem.title.toUpperCase(),
                                            position:
                                              section.items.indexOf(subItem) +
                                              1,
                                            text: subItem.title,
                                          },
                                        });
                                      } else {
                                        tagular("click", {
                                          actionOutcome: "INTERNALLINK",
                                          outboundUrl: subItem.url,
                                          webElement: {
                                            elementType: "LINK",
                                            location: `SUB NAV - ${item.title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                            name: subItem.title.toUpperCase(),
                                            position: String(
                                              section.items.indexOf(subItem) + 1
                                            ),
                                            text: subItem.title,
                                          },
                                        });
                                      }
                                    }}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-black-300 group-hover:text-blue">
                                        {subItem.title}
                                      </span>
                                      <ArrowRight className="w-4 h-4 transition-opacity opacity-0 group-hover:opacity-100 group-hover:text-blue" />
                                    </div>
                                    {subItem.description && (
                                      <div className="mt-1 text-xs text-black">
                                        {subItem.description}
                                      </div>
                                    )}
                                  </a>
                                );
                              }
                              if ("columns" in subItem) {
                                return subItem.columns.map(column => (
                                  <React.Fragment
                                    key={`column-${item.title}-${section.title}-${column.title || "column"}`}
                                  >
                                    {column.title && (
                                      <div className="pt-2 pb-1 text-base font-semibold uppercase">
                                        {column.title}
                                      </div>
                                    )}
                                    {column.items.map(columnItem => (
                                      <a
                                        key={`column-item-${item.title}-${section.title}-${columnItem.title}`}
                                        href={columnItem.url}
                                        className="flex items-center justify-between py-2 text-base uppercase border-b text-black-300 hover:text-blue hover:border-blue hover:border-b border-black-200 last:border-b-0 last:hover:border-b-0 group"
                                        onClick={() => {
                                          // Track column item click
                                          if (columnItem.tracking) {
                                            tagular("product_click", {
                                              actionOutcome:
                                                columnItem.tracking
                                                  .actionOutcome ||
                                                "INTERNALLINK",
                                              outboundUrl: columnItem.url,
                                              product: {
                                                brand:
                                                  columnItem.tracking.product
                                                    .brand,
                                                location: `SUB NAV - ${item.title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                name: columnItem.title.toUpperCase(),
                                                position:
                                                  column.items.indexOf(
                                                    columnItem
                                                  ) + 1,
                                                text: columnItem.title,
                                              },
                                            });
                                          } else {
                                            tagular("click", {
                                              actionOutcome: "INTERNALLINK",
                                              outboundUrl: columnItem.url,
                                              webElement: {
                                                elementType: "LINK",
                                                location: `SUB NAV - ${item.title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                name: columnItem.title.toUpperCase(),
                                                position: String(
                                                  column.items.indexOf(
                                                    columnItem
                                                  ) + 1
                                                ),
                                                text: columnItem.title,
                                              },
                                            });
                                          }
                                        }}
                                      >
                                        {columnItem.title}
                                        <ArrowRight className="w-4 h-4 transition-opacity opacity-0 group-hover:opacity-100 group-hover:text-blue" />
                                      </a>
                                    ))}
                                  </React.Fragment>
                                ));
                              }
                              return null;
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {item.cta && (
                  <div className="border-b border-black-200">
                    <div className="container">
                      <a
                        href={item.cta.url}
                        className="flex items-center justify-between w-full py-4 text-lg text-black uppercase hover:text-blue"
                        onClick={() => {
                          // Track cta click
                          if (item.cta.tracking) {
                            tagular("product_click", {
                              actionOutcome:
                                item.cta.tracking.actionOutcome ||
                                "INTERNALLINK",
                              outboundUrl: item.cta.url,
                              product: {
                                brand: item.cta.tracking.product.brand,
                                location: `SUB NAV - ${item.title.toUpperCase()}`,
                                name: `VIEW ALL ${item.title.toUpperCase()}`,
                                text: item.cta.title.toUpperCase(),
                              },
                            });
                          } else {
                            // Default tracking when no tracking object is provided
                            tagular("click", {
                              actionOutcome: "INTERNALLINK",
                              outboundUrl: item.cta.url,
                              webElement: {
                                elementType: "LINK",
                                location: `SUB NAV - ${item.title.toUpperCase()}`,
                                name: `VIEW ALL ${item.title.toUpperCase()}`,
                                text: item.cta.title.toUpperCase(),
                              },
                            });
                          }
                        }}
                      >
                        {item.cta.title}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </div>
        ) : null
      )}

      {/* Account Panel */}
      {activePanel === "Account" && (
        <div
          data-panel-content="Account"
          className="absolute inset-0 flex flex-col translate-x-0 bg-white"
          id="panel-Account"
          role="region"
          aria-label="Account menu"
        >
          <div className="border-b border-black-200 bg-black-100">
            <div className="container flex items-center justify-between py-3">
              <button
                type="button"
                className="flex items-center text-black uppercase hover:text-blue"
                onClick={handleBackToMain}
                data-panel="Account"
                aria-label="Back to Account"
              >
                <span className="inline-block mr-2 transform rotate-180">
                  <ArrowRight className="w-4 h-4" />
                </span>
                Back
              </button>
              <span className="relative inline-block py-2 px-4 my-1 bg-blue-900 text-white font-semibold uppercase rotate-[-1.5deg]">
                Account
              </span>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto">
            <div className="space-y-1">
              {hasSession ? (
                accountItems.map(accountItem => (
                  <div
                    key={`account-item-${accountItem.title}`}
                    className="border-b border-black-200"
                  >
                    <div className="container">
                      <a
                        href={accountItem.href}
                        className="flex items-center justify-between w-full py-4 text-lg text-black uppercase hover:text-blue"
                        onClick={() => {
                          // Track account item click
                          tagular("click", {
                            actionOutcome: "INTERNALLINK",
                            outboundUrl: accountItem.href,
                            webElement: {
                              elementType: "LINK",
                              location: `SUB NAV - ${accountItem.title.toUpperCase()}`,
                              name: accountItem.title.toUpperCase(),
                              text: accountItem.title,
                            },
                          });
                        }}
                      >
                        {accountItem.title}
                      </a>
                    </div>
                  </div>
                ))
              ) : (
                <div className="border-b border-black-200">
                  <div className="container">
                    <a
                      href="/api/auth/login"
                      className="flex items-center justify-between w-full py-4 text-lg text-black uppercase hover:text-blue"
                      onClick={() => {
                        // Track sign in click
                        tagular("click", {
                          actionOutcome: "INTERNALLINK",
                          outboundUrl: "/api/auth/login",
                          webElement: {
                            elementType: "LINK",
                            location: "SUB NAV - SIGN IN",
                            name: "SIGN IN",
                            text: "Sign In",
                          },
                        });
                      }}
                    >
                      Sign In
                    </a>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </div>
  );
}

MobileNav.propTypes = {
  alertDismissed: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  hasSession: PropTypes.bool,
  disableAlert: PropTypes.bool,
  // onClose prop is here for compatibility with parent component but not used
};

export default MobileNav;
